import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Tabs } from '@zoopla-eng/zds-components/tabs';
import { triggerTracking } from '@zoopla/tracking';
import { Box } from '@zoopla-eng/zds-components/box';
import { AuthMutationHookConsumerProps } from '../../hooks/authMutationHook.types';
import { RegisterForm } from '../RegisterForm';

const SignInForm = dynamic<AuthMutationHookConsumerProps>(
    () => import('../SigninForm').then((mod) => mod.SignInForm),
    {
        ssr: false
    }
);

const signInTitleTn = 'Sign In';
const registerTitleTn = 'Register';

type AuthStepProps = {
    initialMode: 'sign-in' | 'register';
    prefilledEmail?: string;
} & AuthMutationHookConsumerProps;

export const AuthStep = ({
    initialMode,
    trackingData,
    onAuthentication,
    prefilledEmail
}: AuthStepProps) => {
    const [selectedTab, setSelectedTab] = useState(initialMode);

    useEffect(() => {
        const focusTimeout = setTimeout(() => {
            // ideally we could get a ref from the Tabs component in ZDS
            const activeTab: HTMLButtonElement | null =
                tabsComponentRef.current?.querySelector(
                    '[role="tab"][aria-selected="true"]'
                ) || null;

            activeTab?.focus();
        }, 200);

        return () => {
            clearTimeout(focusTimeout);
        };
    }, []);
    const tabsComponentRef = useRef<HTMLDivElement>(null);

    return (
        <div ref={tabsComponentRef}>
            <Tabs
                label="Register or sign in"
                value={selectedTab}
                tabs={[
                    {
                        label: registerTitleTn,
                        value: 'register',
                        component: (
                            <Box pt="S400">
                                <RegisterForm
                                    onAuthentication={onAuthentication}
                                    trackingData={trackingData}
                                    prefilledEmail={prefilledEmail}
                                />
                            </Box>
                        )
                    },
                    {
                        label: signInTitleTn,
                        value: 'sign-in',
                        component: (
                            <Box pt="S400">
                                <SignInForm
                                    onAuthentication={onAuthentication}
                                    trackingData={trackingData}
                                />
                            </Box>
                        )
                    }
                ]}
                alignment={{
                    default: 'fill',
                    bpL100: 'left'
                }}
                onValueChange={(newTabToShow) => {
                    setSelectedTab(newTabToShow);

                    triggerTracking({
                        event: 'GAEvent',
                        eventCategory: 'registration',
                        eventAction:
                            newTabToShow === 'sign-in'
                                ? 'click_to_signin'
                                : 'click_to_register',
                        eventLabel: trackingData?.label
                    });
                }}
            />
        </div>
    );
};
